import React from 'react';
import { Card, Typography, Space, Tooltip, Spin, Button, Progress, Select, Switch } from 'antd';
import { AppstoreOutlined, TableOutlined, OrderedListOutlined, LineChartOutlined, CalendarOutlined } from '@ant-design/icons';
import type { ForecastDaily, DailyForecast, DailyForecastParameter } from '../../api/forecastService';
import type { ForecastField } from '../../api/forecastFieldsService';
import { getParameterValue, getWeatherIconFromCondition, getWeatherCode } from '../../utils/weatherUtils';
import { WindDirectionIcon } from './WindDirectionIcon';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useForecastFields } from '../../hooks/useForecastFields';
import { 
  getFieldRenderConfig, 
  isFieldCombined, 
  parseWindValue,
  parsePercentageValue,
  getFieldIcon,
  DAILY_SPECIAL_FIELDS,
  DAILY_COMBINED_FIELDS,
  type FieldRenderConfig,
} from './fieldRenderers';
import { EditableTextModal } from './EditableTextModal';
import { EditableParameterPopover } from './EditableParameterPopover';
import { WeatherIconPicker } from './WeatherIconPicker';
import { CollapsibleText } from './CollapsibleText';
import { FieldReorderModal } from './FieldReorderModal';
import { ForecastChartView } from './ForecastChartView';

const { Title, Text } = Typography;

// Dynamic table day slot - stores raw parameters for flexible rendering
interface DynamicTableDaySlot {
  date: Date;
  dateStr: string;
  dayStr: string;
  forecastDateValue: string;
  weatherDescription: string | null;
  parameters: DailyForecastParameter[];
}

// Modified indicator component - colored dot badge with blink animation
const ModifiedIndicator: React.FC = () => {
  const blinkStyle = `
    @keyframes blink-dot {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
  `;
  
  return (
    <>
      <style>{blinkStyle}</style>
      <Tooltip title="Modified from previous issued forecast">
        <span style={{ 
          display: 'inline-block',
          marginLeft: 6,
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#1890ff', // Ant Design primary blue
          cursor: 'help',
          verticalAlign: 'middle',
          animation: 'blink-dot 2s ease-in-out infinite',
        }} />
      </Tooltip>
    </>
  );
};

interface DailyForecastViewProps {
  forecasts: ForecastDaily[];
  selectedDate: Date | null;
  showGeneralSituation?: boolean; // Control whether to show general situation in this component
  viewMode?: 'cards' | 'table' | 'chart';
  onViewModeChange?: (mode: 'cards' | 'table' | 'chart') => void;
  // Optional callbacks for root users to edit text fields and parameters
  isEditable?: boolean;
  onSaveWeatherDescription?: (valueDate: string, forecastDate: string, newText: string) => Promise<void>;
  onSaveParameter?: (valueDate: string, forecastDate: string, paramName: string, newValue: string) => Promise<void>;
  // Date filter (opt-in, from event range)
  dateFilterEnabled?: boolean;
  onDateFilterEnabledChange?: (enabled: boolean) => void;
  eventDateRange?: string[];
  selectedFilterDates?: string[];
  onFilterDatesChange?: (dates: string[]) => void;
  statusExtra?: React.ReactNode;
}

// Helper component to handle weather icon with fallback
const WeatherIconWithFallback: React.FC<{
  iconPath: string;
  label: string;
  fallbackEmoji: string;
  size: number;
}> = ({ iconPath, label, fallbackEmoji, size }) => {
  const [hasError, setHasError] = React.useState(false);

  // Generate smart fallback emoji from weather code label if needed
  const getSmartFallback = () => {
    // If we have a valid emoji already, use it
    if (fallbackEmoji && fallbackEmoji !== '❓') {
      return fallbackEmoji;
    }
    
    // Otherwise, generate from the weather code label
    const lower = label.toLowerCase();
    if (lower.includes('thunder') || lower.includes('storm')) return '⛈️';
    if (lower.includes('heavy rain') || lower.includes('violent')) return '🌧️';
    if (lower.includes('rain') || lower.includes('shower') || lower.includes('drizzle')) return '🌧️';
    if (lower.includes('snow') || lower.includes('flurr')) return '🌨️';
    if (lower.includes('fog') || lower.includes('mist')) return '🌫️';
    if (lower.includes('ice') || lower.includes('freezing')) return '🧊';
    if (lower.includes('cloudy') || lower.includes('overcast')) {
      if (lower.includes('partly') || lower.includes('mostly clear')) return '⛅';
      if (lower.includes('mostly')) return '☁️';
      return '☁️';
    }
    if (lower.includes('clear') || lower.includes('sunny')) return '☀️';
    
    return '☀️'; // Default to sunny for unknown
  };

  // If icon failed to load, show smart fallback emoji
  if (hasError) {
    return <span style={{ fontSize: size * 0.67 }}>{getSmartFallback()}</span>;
  }

  // If it's an icon path, try to load it
  if (iconPath.startsWith('/')) {
    return (
      <img 
        src={iconPath}
        alt={label}
        style={{ width: size, height: size }}
        onError={() => setHasError(true)}
      />
    );
  }

  // Otherwise it's already an emoji
  return <span style={{ fontSize: size * 0.67 }}>{iconPath}</span>;
};

export const DailyForecastView: React.FC<DailyForecastViewProps> = ({ 
  forecasts, 
  selectedDate, 
  showGeneralSituation = true,
  viewMode: controlledViewMode,
  onViewModeChange,
  isEditable = false,
  onSaveWeatherDescription,
  onSaveParameter,
  dateFilterEnabled = false,
  onDateFilterEnabledChange,
  eventDateRange = [],
  selectedFilterDates = [],
  onFilterDatesChange,
  statusExtra,
}) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { dailyCardsFields, dailyTableFields, loading: fieldsLoading, createDefaultField, fieldsByDbName } = useForecastFields();
  const isDark = theme === 'dark';
  const timezone = selectedEvent?.circuit?.time_zone;
  
  // Support both controlled and uncontrolled mode
  const [internalViewMode, setInternalViewMode] = React.useState<'cards' | 'table' | 'chart'>('cards');
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  // Field reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = React.useState(false);
  const [savingOrder, setSavingOrder] = React.useState(false);
  const [orderVersion, setOrderVersion] = React.useState(0);

  // Compact cards mode - hides General Situation and Weather Description in each day card
  const [compactCards, setCompactCards] = React.useState(false);

  // Ref for the cards container to attach non-passive wheel handler
  const cardsContainerRef = React.useRef<HTMLDivElement>(null);
  // Ref for the table container to attach non-passive wheel handler
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  // Attach wheel event listener with { passive: false } to enable preventDefault for cards view
  React.useEffect(() => {
    const container = cardsContainerRef.current;
    if (!container || viewMode !== 'cards') return;

    const handleWheel = (e: WheelEvent) => {
      // Allow horizontal scrolling with mouse wheel
      if (e.deltaY !== 0 && container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode]);

  // Attach wheel event listener for table view horizontal scrolling
  React.useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || viewMode !== 'table') return;

    const handleWheel = (e: WheelEvent) => {
      // Check if horizontal scrolling is possible
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      const hasVerticalScroll = container.scrollHeight > container.clientHeight;
      
      // If Shift is held, always scroll horizontally
      if (e.shiftKey && e.deltaY !== 0 && hasHorizontalScroll) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        return;
      }
      
      // If there's horizontal overflow and no vertical overflow, convert vertical wheel to horizontal
      if (hasHorizontalScroll && !hasVerticalScroll && e.deltaY !== 0) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        return;
      }
      
      // If there's horizontal overflow and we're at the vertical scroll boundaries, allow horizontal scrolling
      if (hasHorizontalScroll && e.deltaY !== 0) {
        const isAtTop = container.scrollTop === 0;
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
        
        // If scrolling up at top or down at bottom, allow horizontal scrolling
        if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
          e.preventDefault();
          container.scrollLeft += e.deltaY;
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode]);

  // Get the latest forecast issued BEFORE the selected time (matching legacy behavior)
  const activeForecast = React.useMemo(() => {
    if (!forecasts || forecasts.length === 0) return null;

    // If no date selected, use the absolute latest forecast
    if (!selectedDate) {
      const sorted = [...forecasts].sort((a, b) => {
        return new Date(b.value_date).getTime() - new Date(a.value_date).getTime();
      });
      return sorted[0];
    }

    const selectedTime = selectedDate.getTime();
    
    // Filter forecasts issued before the selected time
    const validForecasts = forecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });

    // If no forecasts were issued before the selected time, return null
    if (validForecasts.length === 0) {
      return null;
    }

    // Sort by value_date descending and take the most recent one before selected time
    const sorted = validForecasts.sort((a, b) => {
      return new Date(b.value_date).getTime() - new Date(a.value_date).getTime();
    });

    const active = sorted[0];
    
    return active;
  }, [forecasts, selectedDate]);

  // Filter daily forecasts to only show selected date and onwards
  // When parent has applied date filter, we may have dates that don't include selectedDate - still show them
  const filteredDailyForecasts = React.useMemo(() => {
    if (!activeForecast || !activeForecast.daily_forecasts) return [];
    
    // If no date selected, show all forecasts
    if (!selectedDate) return activeForecast.daily_forecasts;
    
    // Extract calendar date from selectedDate in the circuit timezone
    const dateStr = selectedDate.toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // Parse "MM/DD/YYYY" format and create UTC timestamp for comparison
    const [month, day, year] = dateStr.split('/').map(Number);
    const selectedCalendarDate = Date.UTC(year, month - 1, day);
    
    return activeForecast.daily_forecasts.filter(df => {
      const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
        ? (df.forecast_date as { value: string }).value
        : df.forecast_date;
      
      // Parse the forecast date as calendar date (YYYY-MM-DD) and create UTC timestamp
      const [year, month, day] = dateValue.split('-').map(Number);
      const forecastCalendarDate = Date.UTC(year, month - 1, day);
      
      // Compare calendar dates (without time component)
      return forecastCalendarDate >= selectedCalendarDate;
    });
  }, [activeForecast, selectedDate, timezone]);

  // Calculate next forecast update time (if available)
  const nextUpdateTime = React.useMemo(() => {
    if (!forecasts || forecasts.length === 0 || !selectedDate) return null;
    
    const selectedTime = selectedDate.getTime();
    
    // Find forecasts issued after the selected time
    const futureForecasts = forecasts.filter(f => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime > selectedTime;
    });
    
    if (futureForecasts.length === 0) return null;
    
    // Sort and get the earliest future forecast
    const sorted = futureForecasts.sort((a, b) => {
      return new Date(a.value_date).getTime() - new Date(b.value_date).getTime();
    });
    
    return new Date(sorted[0].value_date);
  }, [forecasts, selectedDate]);

  // Get all parameter names present in the actual data
  const availableParams = React.useMemo(() => {
    const params = new Set<string>();
    filteredDailyForecasts.forEach(df => {
      df.parameters.forEach(p => params.add(p.name.toLowerCase()));
    });
    return params;
  }, [filteredDailyForecasts]);

  // Track which fields are "secondary" (combined into another field)
  const secondaryFields = React.useMemo(() => {
    const fields = new Set<string>();
    Object.values(DAILY_COMBINED_FIELDS).forEach(f => fields.add(f));
    return fields;
  }, []);

  // Excluded parameter names (special fields, secondary fields)
  const excludedNames = React.useMemo(() => new Set([
    ...Array.from(DAILY_SPECIAL_FIELDS).map(s => s.toLowerCase()),
    ...Array.from(secondaryFields).map(s => s.toLowerCase()),
    // wind_direction is no longer excluded - it renders as its own field with arrow
  ]), [secondaryFields]);

  // Save custom field order to localStorage
  const saveCustomFieldOrder = React.useCallback((fieldDbNames: string[]) => {
    try {
      localStorage.setItem('dailyTableFieldOrder', JSON.stringify(fieldDbNames));
    } catch (error) {
      console.error('Error saving field order to localStorage:', error);
    }
  }, []);

  // Fields to display in TABLE view
  const tableDisplayFields = React.useMemo(() => {
    if (fieldsLoading || filteredDailyForecasts.length === 0) return [];
    
    // Start with configured daily table fields that have data
    const configuredFields = dailyTableFields
      .filter(field => 
        availableParams.has(field.db_name.toLowerCase()) && 
        !isFieldCombined(field.db_name) &&
        !DAILY_SPECIAL_FIELDS.has(field.db_name) &&  // Exclude weather codes (shown in title)
        !secondaryFields.has(field.db_name)  // Exclude secondary fields (min_temp combined with max_temp)
      );
    
    // Also include any parameters in data that aren't in configured fields
    // BUT exclude fields that are marked as hidden in the database
    const configuredDbNames = new Set(dailyTableFields.map(f => f.db_name.toLowerCase()));
    const unconfiguredParams = Array.from(availableParams)
      .filter(name => {
        // Skip if already configured
        if (configuredDbNames.has(name)) return false;
        // Skip if in excluded names
        if (excludedNames.has(name)) return false;
        // Skip if the field exists in the database and is marked as hidden
        const field = fieldsByDbName.get(name);
        if (field && field.hidden) return false;
        return true;
      })
      .map(name => createDefaultField(name, null));
    
    const allFields = [...configuredFields, ...unconfiguredParams];
    
    // Apply custom order from localStorage if available
    // Read directly from localStorage to ensure we get the latest value when orderVersion changes
    let customOrder: string[] = [];
    try {
      const stored = localStorage.getItem('dailyTableFieldOrder');
      if (stored) {
        customOrder = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading field order from localStorage:', error);
    }
    
    if (customOrder.length > 0) {
      // Create a map for quick lookup
      const fieldMap = new Map(allFields.map(f => [f.db_name.toLowerCase(), f]));
      
      // Build ordered array based on custom order
      const orderedFields: ForecastField[] = [];
      const remainingFields = new Set(allFields.map(f => f.db_name.toLowerCase()));
      
      // Add fields in custom order
      customOrder.forEach(dbName => {
        const lowerDbName = dbName.toLowerCase();
        const field = fieldMap.get(lowerDbName);
        if (field) {
          orderedFields.push(field);
          remainingFields.delete(lowerDbName);
        }
      });
      
      // Add any remaining fields that weren't in the custom order
      remainingFields.forEach(dbName => {
        const field = fieldMap.get(dbName);
        if (field) {
          orderedFields.push(field);
        }
      });
      
      return orderedFields;
    }
    
    return allFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyTableFields, fieldsLoading, filteredDailyForecasts, availableParams, secondaryFields, excludedNames, createDefaultField, orderVersion, fieldsByDbName]);

  // Fields to display in CARDS view
  // Note: With card_group, we don't filter out "secondary" fields - grouping handles combined display
  const cardsDisplayFields = React.useMemo(() => {
    if (fieldsLoading || filteredDailyForecasts.length === 0) return [];
    
    // Start with configured daily cards fields that have data
    const configuredFields = dailyCardsFields
      .filter(field => 
        availableParams.has(field.db_name.toLowerCase()) && 
        !isFieldCombined(field.db_name) &&
        !DAILY_SPECIAL_FIELDS.has(field.db_name)  // Exclude weather codes (shown in title)
      );
    
    // Also include any parameters in data that aren't in configured fields
    // BUT exclude fields that are marked as hidden in the database
    const configuredDbNames = new Set(dailyCardsFields.map(f => f.db_name.toLowerCase()));
    const unconfiguredParams = Array.from(availableParams)
      .filter(name => {
        // Skip if already configured
        if (configuredDbNames.has(name)) return false;
        // Skip if in excluded names
        if (excludedNames.has(name)) return false;
        // Skip if the field exists in the database and is marked as hidden
        const field = fieldsByDbName.get(name);
        if (field && field.hidden) return false;
        return true;
      })
      .map(name => createDefaultField(name, null));
    
    return [...configuredFields, ...unconfiguredParams];
  }, [dailyCardsFields, fieldsLoading, filteredDailyForecasts, availableParams, excludedNames, createDefaultField, fieldsByDbName]);

  // Group fields by card_group for cards view
  const groupedCardsFields = React.useMemo(() => {
    const groups: { groupName: string | null; fields: ForecastField[] }[] = [];
    const processedGroups = new Set<string>();
    
    cardsDisplayFields.forEach(field => {
      if (field.card_group) {
        // If this group hasn't been processed yet, collect all fields in this group
        if (!processedGroups.has(field.card_group)) {
          processedGroups.add(field.card_group);
          const groupFields = cardsDisplayFields.filter(f => f.card_group === field.card_group);
          groups.push({ groupName: field.card_group, fields: groupFields });
        }
      } else {
        // Ungrouped field - render individually
        groups.push({ groupName: null, fields: [field] });
      }
    });
    
    return groups;
  }, [cardsDisplayFields]);

  // Prepare table data - store raw parameters for each day slot
  const tableData = React.useMemo(() => {
    if (filteredDailyForecasts.length === 0) return { daySlots: [] as DynamicTableDaySlot[] };
    
    const daySlots: DynamicTableDaySlot[] = filteredDailyForecasts.map((df) => {
      const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
        ? (df.forecast_date as { value: string }).value
        : df.forecast_date;
      
      // Parse the date string as calendar date (YYYY-MM-DD)
      const [year, month, day] = dateValue.split('-').map(Number);
      const forecastDate = new Date(year, month - 1, day);
      
      // Format date strings
      const dateStr = forecastDate.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
      });
      
      const dayStr = forecastDate.toLocaleDateString('en-US', {
        weekday: 'short',
      }).toUpperCase();
      
      return {
        date: forecastDate,
        dateStr,
        dayStr,
        forecastDateValue: dateValue,
        weatherDescription: df.weather_description || null,
        parameters: df.parameters,
      };
    });
    
    return { daySlots };
  }, [filteredDailyForecasts]);

  // ==========================================
  // SVG Visualization Helpers for Table View
  // ==========================================
  
  const renderSparkline = (values: (number | null)[], color: string, height = 30) => {
    const validValues = values.filter((v): v is number => v !== null);
    if (validValues.length === 0) return null;
    
    const max = Math.max(...validValues);
    const min = Math.min(...validValues);
    const range = max - min || 1;
    
    const points = values.map((v, i) => {
      if (v === null) return null;
      const x = (i / (values.length - 1)) * 100;
      const y = ((v - min) / range) * 100;
      return `${x},${100 - y}`;
    }).filter(Boolean).join(' ');
    
    return (
      <svg 
        width="100%" 
        height={height} 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  };

  // ==========================================
  // Table View Dynamic Row Renderers
  // ==========================================

  // Helper to check if a day slot should have a left border (for visual separation between days)
  // For daily forecast, each column is already a day, so we add a border to every column except the first data column
  const isFirstSlotOfDay = (idx: number): boolean => {
    if (idx === 0) return false; // First column is always the parameter name
    return idx > 1; // Add border to all columns after the first data column (idx 1)
  };

  // Common cell styles
  const getHeaderCellStyle = () => ({
    position: 'sticky' as const,
    left: 0,
    backgroundColor: isDark ? '#141414' : '#fafafa',
    padding: '8px',
    borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
    fontWeight: 500,
    minWidth: '200px',
    width: '200px',
    zIndex: 1,
  });

  const getDataCellStyle = () => ({
    padding: '6px',
    borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
    textAlign: 'center' as const,
  });

  const getDayHeaderCellStyle = (isFirstOfDay: boolean = false) => ({
    position: 'sticky' as const,
    top: 0,
    backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
    padding: '8px',
    borderBottom: `2px solid ${isDark ? '#434343' : '#d9d9d9'}`,
    borderLeft: isFirstOfDay ? `2px solid ${isDark ? '#595959' : '#bfbfbf'}` : undefined,
    textAlign: 'center' as const,
    fontWeight: 600,
    minWidth: '120px',
    zIndex: 2,
  });

  // Sparkline row (temperature fields with visual trend line)
  const renderSparklineRow = (field: ForecastField, config: FieldRenderConfig) => {
    const values = tableData.daySlots.map(slot => {
      const param = getParameterValue(slot.parameters, field.db_name);
      return param.value ? parseFloat(param.value) : null;
    });
    
    const color = config.color || '#1890ff';
    
    return (
      <tr key={field.db_name}>
        <td style={{ ...getHeaderCellStyle(), verticalAlign: 'middle' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {field.unit ? `(${field.unit})` : ''}
          </span>
        </td>
        <td
          colSpan={tableData.daySlots.length}
          style={{
            padding: '4px 6px',
            borderBottom: `1px solid ${isDark ? '#434343' : '#f0f0f0'}`,
            position: 'relative',
            height: '32px',
          }}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Sparkline */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
              {renderSparkline(values, color, 24)}
            </div>
            {/* Values overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex' }}>
              {tableData.daySlots.map((_, idx) => {
                const val = values[idx];
                
                return (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {val !== null && (
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: color,
                          backgroundColor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                          padding: '1px 3px',
                          borderRadius: 3,
                        }}
                      >
                        {Math.round(val)}°
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Bar indicator row (percentage fields like rain probability)
  const renderBarRow = (field: ForecastField, config: FieldRenderConfig) => (
    <tr key={field.db_name}>
      <td style={getHeaderCellStyle()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {field.unit ? `(${field.unit})` : ''}
        </span>
      </td>
      {tableData.daySlots.map((slot, idx) => {
        const param = getParameterValue(slot.parameters, field.db_name);
        const value = parsePercentageValue(param.value);
        const color = config.color || '#52c41a';
        
        return (
          <td key={idx} style={getDataCellStyle()}>
            {value !== null ? (
              <div style={{ width: 90, margin: '0 auto' }}>
                <Progress 
                  percent={value} 
                  size="small"
                  strokeColor={color}
                  trailColor={isDark ? '#303030' : '#f0f0f0'}
                  format={(percent) => <span style={{ fontSize: 11, color: isDark ? '#fff' : '#000' }}>{percent}%</span>}
                />
              </div>
            ) : (
              <Text type="secondary">N/A</Text>
            )}
          </td>
        );
      })}
    </tr>
  );

  // Wind row (with color coding - direction is now in its own row)
  // Note: Wind values are stored in m/s but converted to km/h for display
  const renderWindRow = (field: ForecastField, config: FieldRenderConfig) => {
    return (
      <tr key={field.db_name}>
        <td style={getHeaderCellStyle()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} (km/h)
          </span>
        </td>
        {tableData.daySlots.map((slot, idx) => {
          const param = getParameterValue(slot.parameters, field.db_name);
          const speedNum = parseWindValue(param.value); // Converts m/s to km/h
          const bgColor = config.getColor ? config.getColor(speedNum) : 'transparent';
          
          return (
            <td
              key={idx}
              style={{
                ...getDataCellStyle(),
                padding: '8px',
                backgroundColor: bgColor,
              }}
            >
              {speedNum !== null ? (
                <div style={{ fontSize: '12px', color: bgColor !== 'transparent' ? '#fff' : 'inherit' }}>
                  {speedNum.toFixed(1)} km/h
                </div>
              ) : (
                <Text type="secondary">N/A</Text>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Direction row (shows arrow instead of numeric value)
  const renderDirectionRow = (field: ForecastField) => (
    <tr key={field.db_name}>
      <td style={getHeaderCellStyle()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {getFieldIcon(field.db_name, { size: 24 })} {field.display_name}
        </span>
      </td>
      {tableData.daySlots.map((slot, idx) => {
        const param = getParameterValue(slot.parameters, field.db_name);
        const windSpeedParam = getParameterValue(slot.parameters, 'wind_speed');
        const windSpeed = windSpeedParam.value ? parseFloat(windSpeedParam.value) * 3.6 : null; // Convert m/s to km/h
        
        return (
          <td key={idx} style={getDataCellStyle()}>
            {param.value ? (
              <div style={{ 
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <WindDirectionIcon
                  direction={param.value}
                  windSpeed={windSpeed}
                  size={16}
                  showAngle={true}
                />
              </div>
            ) : (
              <Text type="secondary">N/A</Text>
            )}
          </td>
        );
      })}
    </tr>
  );

  // Helper to check if a field is a wind field that needs m/s to km/h conversion
  const isWindField = (dbName: string): boolean => {
    return dbName.includes('wind_speed') || dbName.includes('wind_gust');
  };

  // Simple value row (default for most fields)
  // Note: Wind fields are stored in m/s but displayed in km/h
  const renderValueRow = (field: ForecastField) => {
    const needsWindConversion = isWindField(field.db_name);
    const displayUnit = needsWindConversion ? 'km/h' : field.unit;
    
    return (
      <tr key={field.db_name}>
        <td style={getHeaderCellStyle()}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.db_name, { size: 24 })} {field.display_name} {displayUnit ? `(${displayUnit})` : ''}
          </span>
        </td>
        {tableData.daySlots.map((slot, idx) => {
          const param = getParameterValue(slot.parameters, field.db_name);
          
          // Convert wind values from m/s to km/h for display
          let displayValue = param.value;
          if (param.value !== null && needsWindConversion) {
            const converted = parseWindValue(param.value);
            displayValue = converted !== null ? converted.toFixed(1) : param.value;
          }
          
          return (
            <td key={idx} style={getDataCellStyle()}>
              {param.value !== null ? (
                <div style={{ fontSize: '13px' }}>
                  {displayValue}{displayUnit && !String(displayValue).includes(displayUnit) ? ` ${displayUnit}` : ''}
                </div>
              ) : (
                <Text type="secondary">N/A</Text>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Main row renderer - dispatches to specific renderers based on field config
  const renderTableFieldRow = (field: ForecastField) => {
    const config = getFieldRenderConfig(field);
    
    // Temperature fields now render as separate rows (no longer combined)
    
    switch (config.renderType) {
      case 'sparkline':
        return renderSparklineRow(field, config);
      case 'bar':
        return renderBarRow(field, config);
      case 'wind':
        return renderWindRow(field, config);
      case 'direction':
        return renderDirectionRow(field);
      default:
        return renderValueRow(field);
    }
  };

  if (!activeForecast || filteredDailyForecasts.length === 0) {
    return (
      <Card>
        <Text type="secondary">
          {!activeForecast 
            ? 'No forecast was issued before the selected time'
            : 'No daily forecast data available for the selected date'}
        </Text>
      </Card>
    );
  }

  // Get the general situation from the active forecast
  const generalSituation = activeForecast.general_situation;

  // Wraps a displayed value with EditableParameterPopover when editing is enabled
  const wrapWithEditable = (
    element: React.ReactNode,
    field: ForecastField,
    rawValue: string | null,
    editContext?: { valueDate: string; forecastDate: string },
    storageUnit?: string | null,
  ): React.ReactNode => {
    if (!isEditable || !onSaveParameter || !editContext || rawValue === null) return element;
    return (
      <EditableParameterPopover
        displayName={field.display_name}
        currentValue={rawValue}
        unit={storageUnit ?? field.unit}
        onSave={(newValue) => onSaveParameter(editContext.valueDate, editContext.forecastDate, field.db_name, newValue)}
      >
        {element}
      </EditableParameterPopover>
    );
  };

  // Render a group of fields (or single ungrouped field)
  const renderFieldGroup = (
    groupName: string | null,
    fields: ForecastField[],
    parameters: DailyForecastParameter[],
    editContext?: { valueDate: string; forecastDate: string },
  ) => {
    // Filter to fields that have values
    const fieldsWithValues = fields.filter(field => {
      const param = getParameterValue(parameters, field.db_name);
      return param.value !== null;
    });
    
    if (fieldsWithValues.length === 0) return null;
    
    // For ungrouped fields (single field), render normally
    if (!groupName || fieldsWithValues.length === 1) {
      const field = fieldsWithValues[0];
      return renderSingleField(field, parameters, editContext);
    }
    
    // For grouped fields, render them together
    const hasModified = fieldsWithValues.some(f => getParameterValue(parameters, f.db_name).modified === 1);
    
    // Build the combined value parts
    type ValuePart = { label: string; value: string | number; rawValue: string | null; windSpeed?: number | null; isArrow: boolean; field: ForecastField };
    const valuesParts: ValuePart[] = [];
    
    fieldsWithValues.forEach(field => {
      const param = getParameterValue(parameters, field.db_name);
      // Allow "0" for percentage/probability fields, but skip null/undefined/empty
      const isPercentageField = field.unit === '%' || field.db_name.includes('probability');
      if (param.value === null || param.value === undefined || param.value === '') {
        return;
      }
      // For percentage fields, always include even if value is "0"
      if (!isPercentageField && !param.value) {
        return;
      }
      
      // Check if this is a wind direction field - render as icon component
      if (field.db_name.includes('direction')) {
        const windSpeedParam = getParameterValue(parameters, 'wind_speed');
        const windSpeed: number | null = windSpeedParam.value ? parseFloat(windSpeedParam.value) * 3.6 : null; // Convert m/s to km/h
        valuesParts.push({ label: '', value: param.value, rawValue: param.value, windSpeed, isArrow: true, field });
        return;
      }
      
      // Format value with unit
      // Wind fields: stored in m/s, display in km/h
      const needsWindConversion = isWindField(field.db_name);
      const numValue = parseFloat(param.value);
      const displayValue = needsWindConversion && !isNaN(numValue) ? numValue * 3.6 : numValue;
      const unit = needsWindConversion ? 'km/h' : (field.unit || '');
      const formattedValue = !isNaN(displayValue) ? displayValue.toFixed(1) : param.value;
      
      // Use group_display_name if available, otherwise fall back to display_name
      const label = field.group_display_name || field.display_name;
      
      valuesParts.push({ label, value: `${formattedValue}${unit ? ` ${unit}` : ''}`, rawValue: param.value, isArrow: false, field });
    });
    
    // Capitalize group name for display
    const groupDisplayLabel = groupName.charAt(0).toUpperCase() + groupName.slice(1);
    
    // Separate regular values from arrows (arrows render at the end without comma)
    const regularParts = valuesParts.filter(p => !p.isArrow);
    const arrowParts = valuesParts.filter(p => p.isArrow);
    
    return (
      <div key={groupName} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
        <span style={{ display: 'flex', width: 24, justifyContent: 'center' }}>{getFieldIcon(fieldsWithValues[0].db_name, { size: 24 })}</span>
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>{groupDisplayLabel} </Text>
          <Text strong style={{ fontSize: 13 }}>
            {regularParts.map((part, idx) => (
              <span key={idx}>
                <Tooltip title={part.field.display_name}>
                  {wrapWithEditable(
                    <span style={{ cursor: 'help' }}>
                      {part.label}: {part.value}
                    </span>,
                    part.field, part.rawValue, editContext,
                    isWindField(part.field.db_name) ? 'm/s' : part.field.unit
                  )}
                </Tooltip>
                {idx < regularParts.length - 1 && ', '}
              </span>
            ))}
            {arrowParts.map((part, idx) => (
              <Tooltip key={`arrow-${idx}`} title={part.field.display_name}>
                {wrapWithEditable(
                  <span style={{ marginLeft: 4, cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                    <WindDirectionIcon
                      direction={part.value}
                      windSpeed={part.windSpeed}
                      size={18}
                      showAngle={true}
                    />
                  </span>,
                  part.field, part.rawValue, editContext, 'degrees'
                )}
              </Tooltip>
            ))}
            {hasModified && <ModifiedIndicator />}
          </Text>
        </div>
      </div>
    );
  };

  // Render a single field (ungrouped)
  const renderSingleField = (
    field: ForecastField, 
    parameters: DailyForecastParameter[],
    editContext?: { valueDate: string; forecastDate: string },
  ) => {
    const config = getFieldRenderConfig(field);
    const param = getParameterValue(parameters, field.db_name);
    
    // Skip if no value (null, undefined, or empty string)
    // Note: "0" is a valid value and should be rendered, especially for percentage fields
    if (param.value === null || param.value === undefined || param.value === '') {
      return null;
    }
    
    // Special handling for wind with direction (legacy combined display)
    // Note: Wind values are stored in m/s but converted to km/h for display
    if (config.combinedWith === 'wind_direction') {
      const directionParam = getParameterValue(parameters, 'wind_direction');
      const speedNum = parseWindValue(param.value); // Converts m/s to km/h
      
      return (
        <div key={field.db_name} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{ display: 'flex', width: 24, justifyContent: 'center' }}>{getFieldIcon(field.db_name, { size: 24 })}</span>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>{field.display_name}: </Text>
            <Text strong style={{ fontSize: 13 }}>
              <Tooltip title={field.display_name}>
                {wrapWithEditable(
                  <span style={{ cursor: 'help' }}>
                    {speedNum !== null ? speedNum.toFixed(1) : param.value} km/h
                  </span>,
                  field, param.value, editContext, 'm/s'
                )}
              </Tooltip>
              {directionParam.value && (
                <Tooltip title="Wind Direction">
                  {wrapWithEditable(
                    <span style={{ marginLeft: 4, cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                      <WindDirectionIcon
                        direction={directionParam.value}
                        windSpeed={speedNum}
                        size={18}
                        showAngle={true}
                      />
                    </span>,
                    { ...field, db_name: 'wind_direction', display_name: 'Wind Direction' } as ForecastField,
                    directionParam.value, editContext, 'degrees'
                  )}
                </Tooltip>
              )}
              {(param.modified === 1 || directionParam.modified === 1) && <ModifiedIndicator />}
            </Text>
          </div>
        </div>
      );
    }
    
    // Special handling for direction fields - show arrow instead of value
    if (config.renderType === 'direction') {
      const windSpeedParam = getParameterValue(parameters, 'wind_speed');
      const windSpeed = windSpeedParam.value ? parseFloat(windSpeedParam.value) * 3.6 : null; // Convert m/s to km/h
      return (
        <div key={field.db_name} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{ display: 'flex', width: 24, justifyContent: 'center' }}>{getFieldIcon(field.db_name, { size: 24 })}</span>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>{field.display_name}: </Text>
            <Text strong style={{ fontSize: 18 }}>
              <Tooltip title={field.display_name}>
                {wrapWithEditable(
                  <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                    <WindDirectionIcon
                      direction={param.value}
                      windSpeed={windSpeed}
                      size={18}
                      showAngle={true}
                    />
                  </span>,
                  field, param.value, editContext, 'degrees'
                )}
              </Tooltip>
              {param.modified === 1 && <ModifiedIndicator />}
            </Text>
          </div>
        </div>
      );
    }
    
    // Special handling for percentage values (like rain probability)
    if (field.unit === '%' || field.db_name.includes('probability')) {
      const displayValue = param.value?.includes('%') 
        ? param.value 
        : `${param.value}${field.unit || '%'}`;
      
      return (
        <div key={field.db_name} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{ display: 'flex', width: 24, justifyContent: 'center' }}>{getFieldIcon(field.db_name, { size: 24 })}</span>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>{field.display_name}: </Text>
            <Text strong style={{ fontSize: 13 }}>
              <Tooltip title={field.display_name}>
                {wrapWithEditable(
                  <span style={{ cursor: 'help' }}>{displayValue}</span>,
                  field, param.value, editContext
                )}
              </Tooltip>
              {param.modified === 1 && <ModifiedIndicator />}
            </Text>
          </div>
        </div>
      );
    }
    
    // Default value rendering
    // Handle wind fields: stored in m/s, display in km/h
    const needsWindConversion = isWindField(field.db_name);
    let displayValue: string;
    
    if (needsWindConversion && param.value) {
      const numValue = parseFloat(param.value);
      const convertedValue = !isNaN(numValue) ? (numValue * 3.6).toFixed(1) : param.value;
      displayValue = `${convertedValue} km/h`;
    } else {
      const unit = field.unit || '';
      displayValue = param.value?.includes(unit) && unit
        ? param.value 
        : `${param.value}${unit ? ` ${unit}` : ''}`;
    }
    
    const storageUnit = needsWindConversion ? 'm/s' : field.unit;
    
    return (
      <div key={field.db_name} style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
        <span style={{ display: 'flex', width: 24, justifyContent: 'center' }}>{getFieldIcon(field.db_name, { size: 24 })}</span>
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>{field.display_name}: </Text>
          <Text strong style={{ fontSize: 13 }}>
            <Tooltip title={field.display_name}>
              {wrapWithEditable(
                <span style={{ cursor: 'help' }}>{displayValue}</span>,
                field, param.value, editContext, storageUnit
              )}
            </Tooltip>
            {param.modified === 1 && <ModifiedIndicator />}
          </Text>
        </div>
      </div>
    );
  };

  // Render a single day card
  const renderDayCard = (df: DailyForecast, index: number) => {
    // Handle forecast_date which can be either { value: "2025-10-13" } or just "2025-10-13"
    const dateValue = typeof df.forecast_date === 'object' && df.forecast_date !== null && 'value' in df.forecast_date
      ? (df.forecast_date as { value: string }).value
      : df.forecast_date;
    
    // Parse the date string as calendar date (YYYY-MM-DD)
    // Avoid timezone conversion by using the date components directly
    const [year, month, day] = dateValue.split('-').map(Number);
    const forecastDate = new Date(year, month - 1, day);
    
    // Get weather codes for title (always shown)
    const weatherCodeAm = getParameterValue(df.parameters, 'weather_code_am');
    const weatherCodePm = getParameterValue(df.parameters, 'weather_code_pm');
    
    // Get weather icons from weather codes (AM and PM)
    const weatherCodeAmInfo = weatherCodeAm.value 
      ? getWeatherCode(weatherCodeAm.value)
      : null;
    const weatherCodePmInfo = weatherCodePm.value 
      ? getWeatherCode(weatherCodePm.value)
      : null;
    
    // Fallback to emoji icon from description if no weather code
    const weatherIconEmoji = getWeatherIconFromCondition(df.weather_description);
    
    // Format date - show full format like "TUE, OCT 13"
    const dateStr = forecastDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
    }).toUpperCase();

    return (
      <Card
        key={index}
        style={{
          flex: '0 0 auto',
          minWidth: 280,
          width: 'fit-content',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          borderColor: isDark ? '#434343' : '#d9d9d9',
          borderRadius: 0,
        }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' as const } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* AM/PM Weather Icons - Always shown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              {/* AM Icon */}
              <div style={{ textAlign: 'center', minWidth: 48 }}>
                <Tooltip title={weatherCodeAmInfo?.label || 'Weather'}>
                  {isEditable && onSaveParameter && activeForecast ? (
                    <WeatherIconPicker
                      currentCode={weatherCodeAm.value}
                      codeType="daily"
                      onSelect={(code) => onSaveParameter(activeForecast.value_date, dateValue, 'weather_code_am', code)}
                    >
                      {weatherCodeAmInfo ? (
                        <WeatherIconWithFallback 
                          iconPath={weatherCodeAmInfo.icon}
                          label={weatherCodeAmInfo.label}
                          fallbackEmoji={weatherIconEmoji}
                          size={44}
                        />
                      ) : (
                        <span style={{ fontSize: 32 }}>{weatherIconEmoji}</span>
                      )}
                    </WeatherIconPicker>
                  ) : weatherCodeAmInfo ? (
                    <div style={{ cursor: 'help' }}>
                      <WeatherIconWithFallback 
                        iconPath={weatherCodeAmInfo.icon}
                        label={weatherCodeAmInfo.label}
                        fallbackEmoji={weatherIconEmoji}
                        size={44}
                      />
                    </div>
                  ) : (
                    <span style={{ fontSize: 32, cursor: 'help' }}>{weatherIconEmoji}</span>
                  )}
                </Tooltip>
                <div style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', marginTop: 2 }}>AM</div>
              </div>
              {/* PM Icon */}
              <div style={{ textAlign: 'center', minWidth: 48 }}>
                <Tooltip title={weatherCodePmInfo?.label || weatherCodeAmInfo?.label || 'Weather'}>
                  {isEditable && onSaveParameter && activeForecast ? (
                    <WeatherIconPicker
                      currentCode={weatherCodePm.value}
                      codeType="daily"
                      onSelect={(code) => onSaveParameter(activeForecast.value_date, dateValue, 'weather_code_pm', code)}
                    >
                      {weatherCodePmInfo ? (
                        <WeatherIconWithFallback 
                          iconPath={weatherCodePmInfo.icon}
                          label={weatherCodePmInfo.label}
                          fallbackEmoji={weatherIconEmoji}
                          size={44}
                        />
                      ) : weatherCodeAmInfo ? (
                        <WeatherIconWithFallback 
                          iconPath={weatherCodeAmInfo.icon}
                          label={weatherCodeAmInfo.label}
                          fallbackEmoji={weatherIconEmoji}
                          size={44}
                        />
                      ) : (
                        <span style={{ fontSize: 32 }}>{weatherIconEmoji}</span>
                      )}
                    </WeatherIconPicker>
                  ) : weatherCodePmInfo ? (
                    <div style={{ cursor: 'help' }}>
                      <WeatherIconWithFallback 
                        iconPath={weatherCodePmInfo.icon}
                        label={weatherCodePmInfo.label}
                        fallbackEmoji={weatherIconEmoji}
                        size={44}
                      />
                    </div>
                  ) : weatherCodeAmInfo ? (
                    <div style={{ cursor: 'help' }}>
                      <WeatherIconWithFallback 
                        iconPath={weatherCodeAmInfo.icon}
                        label={weatherCodeAmInfo.label}
                        fallbackEmoji={weatherIconEmoji}
                        size={44}
                      />
                    </div>
                  ) : (
                    <span style={{ fontSize: 32, cursor: 'help' }}>{weatherIconEmoji}</span>
                  )}
                </Tooltip>
                <div style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', marginTop: 2 }}>PM</div>
              </div>
            </div>
            <div>
              <Title level={5} style={{ margin: 0 }}>{dateStr}</Title>
              {(weatherCodeAmInfo || weatherCodePmInfo) && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {weatherCodeAmInfo?.label || weatherCodePmInfo?.label}
                </Text>
              )}
            </div>
          </div>
        }
      >
          {/* Weather Description - hidden in compact mode, collapsed by default */}
          {!compactCards && df.weather_description && (
            <div style={{ marginBottom: 12 }}>
              <CollapsibleText
                content={df.weather_description}
                isDark={isDark}
                compact
                defaultCollapsed
                maxWidth="400px"
                gradientRgb={isDark ? undefined : '255, 255, 255'}
                header={<>
                  {df.weather_description_modified === 1 && (
                    <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                      <ModifiedIndicator />
                    </span>
                  )}
                  {isEditable && onSaveWeatherDescription && activeForecast && (
                    <EditableTextModal
                      title="Daily Forecast"
                      initialValue={df.weather_description}
                      onSave={(newText) => onSaveWeatherDescription(activeForecast.value_date, dateValue, newText)}
                      buttonSize="small"
                    />
                  )}
                </>}
              />
            </div>
          )}

          {/* Dynamic Weather Parameters - Grouped by card_group */}
          {fieldsLoading ? (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Spin size="small" />
            </div>
          ) : (
            <Space orientation="vertical" size={6} style={{ width: '100%' }}>
              {groupedCardsFields.map(group => 
                renderFieldGroup(
                  group.groupName, group.fields, df.parameters,
                  isEditable && onSaveParameter && activeForecast ? { valueDate: activeForecast.value_date, forecastDate: dateValue } : undefined
                )
              )}
            </Space>
          )}
        </Card>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* General Situation - Separate Container (has its own collapse; not affected by Compact) */}
      {showGeneralSituation && generalSituation && (
        <Card
          size="small"
          style={{
            marginBottom: 0,
            backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
            borderColor: isDark ? '#434343' : '#d9d9d9',
            flexShrink: 0,
            borderRadius: 0,
          }}
        >
          <CollapsibleText
            content={generalSituation}
            isDark={isDark}
            defaultCollapsed
            maxWidth="800px"
            header={
              <Text strong style={{ display: 'flex', alignItems: 'center' }}>
                General Situation
                {activeForecast.general_situation_modified === 1 && <ModifiedIndicator />}
              </Text>
            }
          />
        </Card>
      )}

      {/* Controls Container - Issued Label + View Toggle */}
      <Card
        size="small"
        style={{
          marginBottom: 0,
          backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
          borderColor: isDark ? '#434343' : '#d9d9d9',
          flexShrink: 0,
          borderRadius: 0,
        }}
        styles={{ body: { padding: '4px 12px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Issued: {new Date(activeForecast.value_date).toLocaleString(undefined, {
                timeZone: timezone,
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </Text>
            {nextUpdateTime && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Next update: {nextUpdateTime.toLocaleString(undefined, {
                  timeZone: timezone,
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Text>
            )}
            {statusExtra}
          </div>
          <Space wrap>
            {/* Date filter - opt-in switch + multi-select */}
            {eventDateRange.length > 0 && onDateFilterEnabledChange && (
              <Space size="small">
                <Tooltip title="Filter forecast data by date (can impact performance when enabled)">
                  <Space size={4}>
                    <CalendarOutlined style={{ color: dateFilterEnabled ? '#1890ff' : undefined }} />
                    <Switch
                      size="small"
                      checked={dateFilterEnabled}
                      onChange={onDateFilterEnabledChange}
                    />
                    <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                      Filter by date
                    </span>
                  </Space>
                </Tooltip>
                {dateFilterEnabled && onFilterDatesChange && (
                  <>
                    <Select
                      mode="multiple"
                      value={selectedFilterDates}
                      onChange={onFilterDatesChange}
                      style={{ minWidth: 180, maxWidth: 320 }}
                      placeholder="All dates"
                      maxTagCount="responsive"
                      size="small"
                      options={eventDateRange.map(d => ({
                        value: d,
                        label: new Date(d + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
                      }))}
                    />
                    {selectedFilterDates.length < eventDateRange.length && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onFilterDatesChange([...eventDateRange])}
                        style={{ padding: '0 4px', height: 'auto', fontSize: 12 }}
                      >
                        All
                      </Button>
                    )}
                  </>
                )}
              </Space>
            )}
            {/* Compact Cards Toggle - only show in cards view */}
            {viewMode === 'cards' && (
              <Tooltip title="Hide textual forecast in each day card.">
                <Space size={4}>
                  <Switch
                    size="small"
                    checked={compactCards}
                    onChange={setCompactCards}
                  />
                  <span style={{ fontSize: '12px', color: isDark ? '#d9d9d9' : '#595959' }}>
                    Compact
                  </span>
                </Space>
              </Tooltip>
            )}
            {viewMode === 'table' && (
              <Button
                size="small"
                icon={<OrderedListOutlined />}
                onClick={() => setIsReorderModalOpen(true)}
              >
                Reorder Fields
              </Button>
            )}
            <Space.Compact>
              <Button
                size="small"
                type={viewMode === 'cards' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('cards')}
              >
                Cards
              </Button>
              <Button
                size="small"
                type={viewMode === 'table' ? 'primary' : 'default'}
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
              >
                Table
              </Button>
              <Button
                size="small"
                type={viewMode === 'chart' ? 'primary' : 'default'}
                icon={<LineChartOutlined />}
                onClick={() => setViewMode('chart')}
              >
                Chart
              </Button>
            </Space.Compact>
          </Space>
        </div>
      </Card>

      {/* Table View - Dynamic Fields */}
      {viewMode === 'table' && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`,
            borderRadius: 8,
            backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          }}
        >
          {fieldsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin tip="Loading field configuration..." />
            </div>
          ) : (
            <div 
              ref={tableContainerRef}
              style={{ 
                flex: 1, 
                minHeight: 0, 
                overflow: 'auto',
                scrollbarWidth: 'thin',
              }}
            >
              <table
                style={{
                  width: 'max-content',
                  minWidth: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  color: isDark ? '#d9d9d9' : '#262626',
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
                        padding: '8px',
                        borderBottom: `2px solid ${isDark ? '#434343' : '#d9d9d9'}`,
                        textAlign: 'left',
                        fontWeight: 600,
                        minWidth: '200px',
                        width: '200px',
                        zIndex: 3,
                      }}
                    >
                      Parameter
                    </th>
                    {tableData.daySlots.map((slot, idx) => {
                      const weatherCodeAm = getParameterValue(slot.parameters, 'weather_code_am');
                      const weatherCodePm = getParameterValue(slot.parameters, 'weather_code_pm');
                      const weatherCodeAmInfo = weatherCodeAm.value ? getWeatherCode(weatherCodeAm.value) : null;
                      const weatherCodePmInfo = weatherCodePm.value ? getWeatherCode(weatherCodePm.value) : null;
                      const fallbackEmoji = slot.weatherDescription ? getWeatherIconFromCondition(slot.weatherDescription) : '☀️';
                      const isFirstOfDay = isFirstSlotOfDay(idx + 1); // +1 because idx 0 is the parameter header
                      
                      return (
                        <th
                          key={idx}
                          style={getDayHeaderCellStyle(isFirstOfDay)}
                        >
                          {/* Weather Icons - AM/PM */}
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
                            <div style={{ textAlign: 'center' }}>
                              <Tooltip title={weatherCodeAmInfo?.label || 'Weather AM'}>
                                {weatherCodeAmInfo ? (
                                  <WeatherIconWithFallback 
                                    iconPath={weatherCodeAmInfo.icon}
                                    label={weatherCodeAmInfo.label}
                                    fallbackEmoji={fallbackEmoji}
                                    size={28}
                                  />
                                ) : (
                                  <span style={{ fontSize: 20 }}>{fallbackEmoji}</span>
                                )}
                              </Tooltip>
                              <div style={{ fontSize: 8, color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>AM</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <Tooltip title={weatherCodePmInfo?.label || weatherCodeAmInfo?.label || 'Weather PM'}>
                                {weatherCodePmInfo ? (
                                  <WeatherIconWithFallback 
                                    iconPath={weatherCodePmInfo.icon}
                                    label={weatherCodePmInfo.label}
                                    fallbackEmoji={fallbackEmoji}
                                    size={28}
                                  />
                                ) : weatherCodeAmInfo ? (
                                  <WeatherIconWithFallback 
                                    iconPath={weatherCodeAmInfo.icon}
                                    label={weatherCodeAmInfo.label}
                                    fallbackEmoji={fallbackEmoji}
                                    size={28}
                                  />
                                ) : (
                                  <span style={{ fontSize: 20 }}>{fallbackEmoji}</span>
                                )}
                              </Tooltip>
                              <div style={{ fontSize: 8, color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>PM</div>
                            </div>
                          </div>
                          {/* Date */}
                          <div style={{ fontSize: '11px' }}>{slot.dayStr}</div>
                          <div style={{ fontSize: '13px' }}>{slot.dateStr}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Dynamic field rows */}
                  {tableDisplayFields.map(field => renderTableFieldRow(field))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cards View - Daily Forecast Cards - Horizontal Scrollable */}
      {viewMode === 'cards' && (
        <div
          ref={cardsContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'auto',
            gap: 0,
            paddingBottom: 0,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
          }}
        >
          {fieldsLoading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <Spin size="large" tip="Loading field configuration..." />
            </div>
          ) : (
            filteredDailyForecasts.map((df, index) => renderDayCard(df, index))
          )}
        </div>
      )}

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ForecastChartView
            forecasts={forecasts}
            selectedDate={selectedDate}
            forecastType="daily"
            availableFields={tableDisplayFields}
          />
        </div>
      )}

      {/* Field Reorder Modal */}
      <FieldReorderModal
        open={isReorderModalOpen}
        fields={tableDisplayFields}
        onSave={(orderedFields) => {
          setSavingOrder(true);
          try {
            const dbNames = orderedFields.map(f => f.db_name);
            saveCustomFieldOrder(dbNames);
            setOrderVersion(v => v + 1); // Trigger re-render
            setIsReorderModalOpen(false);
          } catch (error) {
            console.error('Error saving field order:', error);
          } finally {
            setSavingOrder(false);
          }
        }}
        onCancel={() => setIsReorderModalOpen(false)}
        onReset={() => {
          localStorage.removeItem('dailyTableFieldOrder');
          setOrderVersion(v => v + 1); // Trigger re-render
          setIsReorderModalOpen(false);
        }}
        saving={savingOrder}
        title="Reorder Daily Forecast Fields"
      />
    </div>
  );
};
